// scheduling/pages/BugReportsTab.jsx
// Dev triage surface for Feedback.BugReports (ContextApp='rprs'). Rendered as
// the AdminConsole "Bug Reports" tab, gated on isDevSuperUser. Lists reports
// (scope='all'), expands a row for full detail + captured diagnostics, and
// resolves one or many (per-row or bulk) — each resolve writes the workflow
// columns via func-shared-services and notifies the reporter in-app.
//
// Data: services/bugReports — fetchBugReports / resolveBugReports / notifyBugResolved.

import { useState, useEffect, useCallback } from 'react';
import { fetchBugReports, resolveBugReports, notifyBugResolved } from '../services/bugReports';
import { BRAND } from '../utils/schedConstants';

const STATUS_META = {
  open:        { label: 'Open',        bg: '#FEF3C7', fg: '#92400E' },
  triaged:     { label: 'Triaged',     bg: '#DBEAFE', fg: '#1D4ED8' },
  in_progress: { label: 'In Progress', bg: '#E0E7FF', fg: '#3730A3' },
  resolved:    { label: 'Resolved',    bg: '#D1FAE5', fg: '#065F46' },
  wont_fix:    { label: "Won't Fix",   bg: '#F3F4F6', fg: '#6B7280' },
};
const STATUS_KEYS = ['open', 'triaged', 'in_progress', 'resolved', 'wont_fix'];
// Statuses that notify the reporter when reached.
const NOTIFY_STATUSES = new Set(['resolved', 'wont_fix']);

function fmtAge(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      background: m.bg, color: m.fg, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

export default function BugReportsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [search, setSearch] = useState('');

  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);       // full report for expandedId
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDiag, setShowDiag] = useState(false);   // logs/events/context disclosure

  // Per-row resolve form (single expanded row at a time).
  const [form, setForm] = useState({ status: 'resolved', note: '', ref: '' });
  const [actioning, setActioning] = useState(false);

  // Bulk selection.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState('resolved');
  const [bulkNote, setBulkNote] = useState('');

  const load = useCallback(() => {
    setLoading(true); setError(null);
    fetchBugReports({ scope: 'all', status: statusFilter === 'all' ? undefined : statusFilter })
      .then(res => setList(res.reports || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Collapse + clear selection whenever the filter changes (ids may leave the list).
  useEffect(() => { setExpandedId(null); setSelected(new Set()); }, [statusFilter]);

  const openRow = (r) => {
    if (expandedId === r.bugReportId) { setExpandedId(null); return; }
    setExpandedId(r.bugReportId);
    setForm({ status: r.status === 'open' ? 'resolved' : r.status, note: r.resolutionNote || '', ref: r.addressedInRef || '' });
    setShowDiag(false);
    setDetail(null);
    setDetailLoading(true);
    fetchBugReports({ scope: 'all', id: r.bugReportId })
      .then(res => setDetail(res.report || null))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  // Resolve a set of ids to a status, then notify the affected reporters.
  const doResolve = async (ids, status, resolutionNote, addressedInRef) => {
    const res = await resolveBugReports({ ids, status, resolutionNote, addressedInRef });
    const resolved = res.resolved || [];
    const toNotify = resolved.filter(b => NOTIFY_STATUSES.has(b.status));
    if (toNotify.length) {
      // Soft-fail: the resolution already landed; a notify hiccup shouldn't error the action.
      try { await notifyBugResolved(toNotify); } catch (_) { /* ignore */ }
    }
    return resolved.length;
  };

  const handleRowResolve = async () => {
    if (!expandedId) return;
    setActioning(true);
    try {
      await doResolve([expandedId], form.status, form.note, form.ref);
      setExpandedId(null);
      load();
    } catch (e) {
      alert('Resolve failed: ' + e.message);
    } finally {
      setActioning(false);
    }
  };

  const handleBulkResolve = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const m = STATUS_META[bulkStatus];
    if (!window.confirm(`Set ${ids.length} report${ids.length > 1 ? 's' : ''} to "${m.label}"${NOTIFY_STATUSES.has(bulkStatus) ? ' and notify the reporter(s)' : ''}?`)) return;
    setActioning(true);
    try {
      await doResolve(ids, bulkStatus, bulkNote || undefined);
      setSelected(new Set());
      setBulkNote('');
      load();
    } catch (e) {
      alert('Bulk resolve failed: ' + e.message);
    } finally {
      setActioning(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Client-side search over title / submitter / route.
  const q = search.trim().toLowerCase();
  const rows = q
    ? list.filter(r =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.submittedBy || '').toLowerCase().includes(q) ||
        (r.route || '').toLowerCase().includes(q))
    : list;

  const TH = { padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 };

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: BRAND.deepTeal }}>Bug Reports</h3>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, lineHeight: 1.5 }}>
            User-submitted reports for this app. Expand a row to read the full report + captured diagnostics,
            then set a status. Marking <strong>Resolved</strong> or <strong>Won't Fix</strong> notifies the reporter in-app.
          </div>
        </div>
        <button onClick={load} disabled={loading}
          style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer',
            padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {/* Filter + search */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12, fontWeight: 600, color: '#374151' }}>
          <option value="all">All statuses</option>
          {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title / submitter / route…"
          style={{ flex: 1, minWidth: 200, padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12 }} />
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>{rows.length} shown</span>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', marginBottom: 12,
          background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#1D4ED8', fontSize: 12 }}>{selected.size} selected</span>
          <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #BFDBFE', fontSize: 12, fontWeight: 600 }}>
            {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <input value={bulkNote} onChange={e => setBulkNote(e.target.value)} placeholder="Shared note for the reporter(s) (optional)…"
            style={{ flex: 1, minWidth: 200, padding: '5px 8px', borderRadius: 6, border: '1px solid #BFDBFE', fontSize: 12 }} />
          <button onClick={handleBulkResolve} disabled={actioning}
            style={{ background: BRAND.deepTeal, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
              padding: '6px 14px', fontSize: 12, fontWeight: 700 }}>
            {actioning ? 'Applying…' : `Apply to ${selected.size}`}
          </button>
          <button onClick={() => setSelected(new Set())} disabled={actioning}
            style={{ background: 'transparent', color: '#6B7280', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Clear
          </button>
        </div>
      )}

      {/* List */}
      {error ? (
        <div style={{ padding: 16, color: '#DC2626', fontSize: 13 }}>Failed to load: {error}</div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>{'🐛'}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#9CA3AF' }}>No bug reports{statusFilter !== 'all' ? ` in "${STATUS_META[statusFilter].label}"` : ''}</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                <th style={{ ...TH, width: 34, textAlign: 'center' }}></th>
                <th style={TH}>Title</th>
                <th style={TH}>Submitted by</th>
                <th style={TH}>Status</th>
                <th style={TH}>When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isExpanded = expandedId === r.bugReportId;
                return (
                  <Row key={r.bugReportId}
                    r={r} isExpanded={isExpanded} TH={TH}
                    checked={selected.has(r.bugReportId)}
                    onCheck={() => toggleSelect(r.bugReportId)}
                    onOpen={() => openRow(r)}
                    detail={isExpanded ? detail : null}
                    detailLoading={isExpanded && detailLoading}
                    showDiag={showDiag} setShowDiag={setShowDiag}
                    form={form} setForm={setForm}
                    actioning={actioning}
                    onResolve={handleRowResolve}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Single row + its expanded detail/resolve panel ──────────────────────────
function Row({ r, isExpanded, checked, onCheck, onOpen, detail, detailLoading, showDiag, setShowDiag, form, setForm, actioning, onResolve }) {
  return (
    <>
      <tr style={{ borderTop: '1px solid #F3F4F6', background: isExpanded ? '#F9FAFB' : '#fff' }}>
        <td style={{ padding: '10px 0', textAlign: 'center' }}>
          <input type="checkbox" checked={checked} onChange={onCheck} onClick={e => e.stopPropagation()} />
        </td>
        <td style={{ padding: '10px 12px', cursor: 'pointer' }} onClick={onOpen}>
          <div style={{ fontWeight: 600, color: '#111827' }}>
            <span style={{ color: '#9CA3AF', fontWeight: 500, marginRight: 6 }}>{isExpanded ? '▾' : '▸'}</span>
            {r.title || '(no title)'}
          </div>
          {r.route && <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1, marginLeft: 18 }}>{r.route}</div>}
        </td>
        <td style={{ padding: '10px 12px', color: '#374151' }}>{r.submittedBy || '—'}</td>
        <td style={{ padding: '10px 12px' }}><StatusPill status={r.status} /></td>
        <td style={{ padding: '10px 12px', color: '#6B7280', whiteSpace: 'nowrap' }}>{fmtAge(r.createdAt)}</td>
      </tr>
      {isExpanded && (
        <tr style={{ background: '#F9FAFB' }}>
          <td colSpan={5} style={{ padding: '0 12px 16px 46px' }}>
            {detailLoading ? (
              <div style={{ padding: 16, color: '#9CA3AF', fontSize: 12 }}>Loading detail…</div>
            ) : (
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16 }}>
                {/* Report body */}
                <Field label="What were you trying to do?" value={(detail || r).doing} />
                <Field label="What happened instead?" value={(detail || r).happened} />
                <Field label="Steps to reproduce" value={(detail || r).repro} />
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, lineHeight: 1.6 }}>
                  {(detail || r).url && <div><strong>URL:</strong> {(detail || r).url}</div>}
                  {(detail || r).userAgent && <div><strong>Browser:</strong> {(detail || r).userAgent}</div>}
                  {r.reviewedBy && <div><strong>Last reviewed by:</strong> {r.reviewedBy} {r.reviewedAt ? `(${fmtAge(r.reviewedAt)})` : ''}</div>}
                  {r.addressedInRef && <div><strong>Addressed in:</strong> {r.addressedInRef}</div>}
                </div>

                {/* Diagnostics disclosure */}
                {detail && (detail.logs || detail.events || detail.context) && (
                  <div style={{ marginTop: 10 }}>
                    <button onClick={() => setShowDiag(v => !v)}
                      style={{ background: 'transparent', border: 'none', color: '#3B82F6', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: 0 }}>
                      {showDiag ? '▾ Hide' : '▸ Show'} captured diagnostics
                      {(() => {
                        const parts = [];
                        if (Array.isArray(detail.logs)) parts.push(`${detail.logs.length} logs`);
                        if (Array.isArray(detail.events)) parts.push(`${detail.events.length} events`);
                        return parts.length ? ` (${parts.join(', ')})` : '';
                      })()}
                    </button>
                    {showDiag && (
                      <div style={{ marginTop: 8 }}>
                        <Diag label="Context" data={detail.context} />
                        <Diag label="Console logs" data={detail.logs} />
                        <Diag label="Window events" data={detail.events} />
                      </div>
                    )}
                  </div>
                )}

                {/* Resolve form */}
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #F3F4F6' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280' }}>Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12, fontWeight: 600 }}>
                      {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                    </select>
                    <input value={form.ref} onChange={e => setForm(f => ({ ...f, ref: e.target.value }))}
                      placeholder="Addressed in (commit / PR / branch)"
                      style={{ flex: 1, minWidth: 180, padding: '5px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12 }} />
                  </div>
                  <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                    placeholder={NOTIFY_STATUSES.has(form.status) ? 'Resolution note — shown to the reporter in their notification' : 'Internal note (only shown to the reporter on resolve / won’t-fix)'}
                    rows={2}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button onClick={onResolve} disabled={actioning}
                      style={{ background: BRAND.deepTeal, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
                        padding: '7px 16px', fontSize: 12, fontWeight: 700 }}>
                      {actioning ? 'Saving…' : NOTIFY_STATUSES.has(form.status) ? 'Save & notify reporter' : 'Save status'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Diag({ label, data }) {
  if (data == null) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>{label}</div>
      <pre style={{ margin: 0, background: '#0F172A', color: '#E2E8F0', borderRadius: 6, padding: 10, fontSize: 10.5,
        maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}
